import {
  AgentCheckResponse,
  AutopilotDetails,
  AutopilotMetrics,
  AutopilotProject,
  DisableAutopilotResponse,
  EnableAutopilotResponse,
  SetAgentRoleResponse,
  type AgentReadCommand,
  type AgentRole,
  type AutopilotAgent,
  type AutopilotRange,
} from "@cloudserver/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client.js";

/*
 * Autopilot (docs/autopilot.md): the agents of a project, one server's
 * reports and charts, turning it on and off. Keys sit under `projects`, so
 * project events and our own writes refresh them.
 */

/** While an agent is being installed, look again soon; otherwise twice a minute. */
const WAITING_REFRESH_MS = 5_000;
const REFRESH_MS = 30_000;
const waiting = (agent: AutopilotAgent | null | undefined) =>
  agent?.status === "waiting" || agent?.status === "connected";

const keys = {
  project: (projectId: string) => ["projects", projectId, "autopilot"] as const,
  server: (projectId: string, serverId: string) =>
    ["projects", projectId, "autopilot", serverId] as const,
};

/** Every agent of the project (status, health, latest numbers) and whether Autopilot is offered. */
export const useAutopilotProject = (projectId: string) =>
  useQuery({
    queryKey: keys.project(projectId),
    queryFn: () => api(`/projects/${projectId}/autopilot`, { schema: AutopilotProject }),
    refetchInterval: (query) =>
      query.state.data?.agents.some(waiting) ? WAITING_REFRESH_MS : REFRESH_MS,
  });

/** One agent from the project's list, or null when the server has none. */
export function useAutopilotAgent(projectId: string, serverId: string) {
  const query = useAutopilotProject(projectId);
  return {
    available: query.data?.available ?? false,
    agent: query.data?.agents.find((a) => a.serverId === serverId) ?? null,
  };
}

export const useAutopilotDetails = (projectId: string, serverId: string) =>
  useQuery({
    queryKey: keys.server(projectId, serverId),
    queryFn: () =>
      api(`/projects/${projectId}/servers/${serverId}/autopilot`, { schema: AutopilotDetails }),
    refetchInterval: (query) =>
      waiting(query.state.data?.agent) ? WAITING_REFRESH_MS : REFRESH_MS,
  });

export const useAutopilotMetrics = (
  projectId: string,
  serverId: string,
  range: AutopilotRange,
  enabled: boolean,
) =>
  useQuery({
    queryKey: [...keys.server(projectId, serverId), "metrics", range],
    queryFn: () =>
      api(`/projects/${projectId}/servers/${serverId}/autopilot/metrics?range=${range}`, {
        schema: AutopilotMetrics,
      }),
    enabled,
    refetchInterval: 60_000,
  });

function useAutopilotMutation<T, R>(projectId: string, fn: (input: T) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.project(projectId) }),
  });
}

/** Turns Autopilot on (or gives a new install command): the answer holds a one-time token. */
export const useEnableAutopilot = (projectId: string, serverId: string) =>
  useAutopilotMutation(projectId, (input: { containers: boolean; role: AgentRole }) =>
    api(`/projects/${projectId}/servers/${serverId}/autopilot`, {
      method: "POST",
      body: input,
      schema: EnableAutopilotResponse,
    }),
  );

/** Chooses the agent role (docs/autopilot.md "Agent roles"); the answer's command brings the server in line. */
export const useSetAgentRole = (projectId: string, serverId: string) =>
  useAutopilotMutation(projectId, (role: AgentRole) =>
    api(`/projects/${projectId}/servers/${serverId}/autopilot/role`, {
      method: "PUT",
      body: { role },
      schema: SetAgentRoleResponse,
    }),
  );

/** «Проверить роль»: one read command of the helper; its answer about itself refreshes the tab. */
export const useAgentCheck = (projectId: string, serverId: string) =>
  useAutopilotMutation(projectId, (command: AgentReadCommand) =>
    api(`/projects/${projectId}/servers/${serverId}/autopilot/agent/check`, {
      method: "POST",
      body: { command, args: {} },
      schema: AgentCheckResponse,
    }),
  );

export const useDisableAutopilot = (projectId: string, serverId: string) =>
  useAutopilotMutation(projectId, () =>
    api(`/projects/${projectId}/servers/${serverId}/autopilot`, {
      method: "DELETE",
      schema: DisableAutopilotResponse,
    }),
  );
